import { use } from 'react';
import { IconDotsVertical, IconLogout } from '@tabler/icons-react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { SidebarMenu, SidebarMenuButton, SidebarMenuItem, useSidebar } from '@/components/ui/sidebar';
import { ForwardTo, LoadingSkeleton } from '@/lib/utils/utils';
import { AuthContext } from '@/lib/context';
import useUser from '@/lib/hooks/use-user';
import { Analytics } from '@/lib/analytics';

export default function SidebarUser() {
    const { isMobile } = useSidebar();
    const { user, isUserLoading } = useUser();
    const auth = use(AuthContext);

    if (isUserLoading) {
        return <LoadingSkeleton />;
    }
    if (!user) {
        return (
            <SidebarMenu>
                <SidebarMenuItem>
                    <SidebarMenuButton asChild size="lg" className="bg-primary/10 text-primary hover:bg-primary/20 transition-colors">
                        <a href="/login" className="flex items-center gap-2">
                            <Avatar className="h-8 w-8 rounded-lg">
                                <AvatarFallback className="bg-primary/20 text-primary">?</AvatarFallback>
                            </Avatar>
                            <div className="grid flex-1 text-left text-sm leading-tight">
                                <span className="truncate font-semibold">Sign In</span>
                                <span className="text-muted-foreground truncate text-xs">Save your progress</span>
                            </div>
                        </a>
                    </SidebarMenuButton>
                </SidebarMenuItem>
            </SidebarMenu>
        );
    }

    const name = user.isAnonymous ? 'Guest Explorer' : (user.displayName || 'User');

    return (
        <SidebarMenu>
            <SidebarMenuItem>
                <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                        <SidebarMenuButton size="lg" className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground">
                            <Avatar className="h-8 w-8 rounded-lg grayscale">
                                <AvatarImage src={user.photoURL} alt={name} />
                                <AvatarFallback className="rounded-lg">{name.split(' ').slice(0, 2).map(n => n[0]).join('')}</AvatarFallback>
                            </Avatar>
                            <div className="grid flex-1 text-left text-sm leading-tight">
                                <span className="truncate font-medium">{name}</span>
                                {user.email && <span className="text-muted-foreground truncate text-xs">{user.email}</span>}
                            </div>
                            <IconDotsVertical className="ml-auto size-4" />
                        </SidebarMenuButton>
                    </DropdownMenuTrigger>

                    <DropdownMenuContent className="w-(--radix-dropdown-menu-trigger-width) min-w-56 rounded-lg" side={isMobile ? 'bottom' : 'right'} align="end" sideOffset={4}>
                        <DropdownMenuItem onClick={() => {
                            Analytics.userLogout();
                            auth.signOut();
                        }}>
                            <IconLogout />
                            Log out
                        </DropdownMenuItem>
                    </DropdownMenuContent>
                </DropdownMenu>
            </SidebarMenuItem>
        </SidebarMenu>
    );
}
